import React, { useEffect, useState } from 'react';

import Icon from '../../../components/AppIcon';
import { careersService } from '../../../services/careersService';

const CareersManager = () => {
	const [careers, setCareers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [editingCareer, setEditingCareer] = useState(null);
	const [showAddForm, setShowAddForm] = useState(false);
	const [formData, setFormData] = useState({
		title: '',
		slug: '',
		department: '',
		location: '',
		employment_type: 'full_time',
		description: '',
		requirements: [],
		responsibilities: [],
		benefits: [],
		salary_range: '',
		experience_level: '',
		education_requirements: '',
		application_deadline: '',
		is_active: true,
		is_featured: false,
		display_order: 0,
	});

	useEffect(() => {
		loadCareers();
	}, []);

	const loadCareers = async () => {
		try {
			setLoading(true);
			const { data, error } = await careersService.getCareers(true); // adminMode = true
			if (error) throw error;
			setCareers(Array.isArray(data) ? data : []);
		} catch (error) {
			console.error('Error loading careers:', error);
			setCareers([]);
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			// Generate slug if not provided
			if (!formData.slug) {
				formData.slug = careersService.generateSlug(formData.title);
			}

			// Convert arrays from strings
			const processedData = {
				...formData,
				requirements: formData.requirements.filter((req) => req.trim()),
				responsibilities: formData.responsibilities.filter((resp) => resp.trim()),
				benefits: formData.benefits.filter((benefit) => benefit.trim()),
			};

			if (editingCareer) {
				// Update existing career
				const result = await careersService.updateCareer(editingCareer.id, processedData);
				if (result.error) throw result.error;
				alert('Career updated successfully!');
			} else {
				// Create new career
				const result = await careersService.createCareer(processedData);
				if (result.error) throw result.error;
				alert('Career created successfully!');
			}

			resetForm();
			loadCareers();
		} catch (error) {
			console.error('Error saving career:', error);
			alert('Error saving career: ' + error.message);
		}
	};

	const handleEdit = (career) => {
		setEditingCareer(career);
		setFormData({
			...career,
			requirements: career.requirements || [],
			responsibilities: career.responsibilities || [],
			benefits: career.benefits || [],
		});
		setShowAddForm(true);
	};

	const handleDelete = async (careerId) => {
		if (!confirm('Are you sure you want to delete this career?')) {
			return;
		}

		try {
			await careersService.deleteCareer(careerId);
			alert('Career deleted successfully!');
			loadCareers();
		} catch (error) {
			console.error('Error deleting career:', error);
			alert('Error deleting career: ' + error.message);
		}
	};

	const resetForm = () => {
		setFormData({
			title: '',
			slug: '',
			department: '',
			location: '',
			employment_type: 'full_time',
			description: '',
			requirements: [],
			responsibilities: [],
			benefits: [],
			salary_range: '',
			experience_level: '',
			education_requirements: '',
			application_deadline: '',
			is_active: true,
			is_featured: false,
			display_order: 0,
		});
		setEditingCareer(null);
		setShowAddForm(false);
	};

	const addArrayItem = (field) => {
		setFormData((prev) => ({
			...prev,
			[field]: [...prev[field], ''],
		}));
	};

	const updateArrayItem = (field, index, value) => {
		setFormData((prev) => ({
			...prev,
			[field]: prev[field].map((item, i) => (i === index ? value : item)),
		}));
	};

	const removeArrayItem = (field, index) => {
		setFormData((prev) => ({
			...prev,
			[field]: prev[field].filter((_, i) => i !== index),
		}));
	};

	return (
		<div className='space-y-6'>
			{/* Header */}
			<div className='bg-white shadow rounded-lg p-6'>
				<div className='flex items-center justify-between'>
					<div>
						<h1 className='text-2xl font-bold text-gray-900'>Careers Manager</h1>
						<p className='text-gray-600'>Manage job postings and career opportunities</p>
					</div>
					<button
						onClick={() => setShowAddForm(true)}
						className='bg-[#800020] text-white px-4 py-2 rounded-md hover:bg-[#6b001a] transition-colors flex items-center space-x-2'
					>
						<Icon name='Plus' size={16} />
						<span>Add Career</span>
					</button>
				</div>
			</div>

			{/* Add/Edit Form */}
			{showAddForm && (
				<div className='bg-white shadow rounded-lg p-6'>
					<h2 className='text-lg font-semibold text-gray-900 mb-4'>
						{editingCareer ? 'Edit Career' : 'Add New Career'}
					</h2>

					<form onSubmit={handleSubmit} className='space-y-4'>
						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Title *</label>
								<input
									type='text'
									value={formData.title}
									onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									required
								/>
							</div>

							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Slug</label>
								<input
									type='text'
									value={formData.slug}
									onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									placeholder='senior-associate-attorney'
								/>
							</div>
						</div>

						<div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Department *</label>
								<input
									type='text'
									value={formData.department}
									onChange={(e) => setFormData((prev) => ({ ...prev, department: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									placeholder='Legal'
									required
								/>
							</div>

							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Location *</label>
								<input
									type='text'
									value={formData.location}
									onChange={(e) => setFormData((prev) => ({ ...prev, location: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									placeholder='Port Harcourt, Nigeria'
									required
								/>
							</div>

							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>
									Employment Type
								</label>
								<select
									value={formData.employment_type}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, employment_type: e.target.value }))
									}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								>
									<option value='full_time'>Full Time</option>
									<option value='part_time'>Part Time</option>
									<option value='contract'>Contract</option>
									<option value='internship'>Internship</option>
								</select>
							</div>
						</div>

						<div>
							<label className='block text-sm font-medium text-gray-700 mb-1'>Description *</label>
							<textarea
								value={formData.description}
								onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
								rows={4}
								className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								placeholder='Job description...'
								required
							/>
						</div>

						{/* Requirements */}
						<div>
							<label className='block text-sm font-medium text-gray-700 mb-1'>Requirements</label>
							{formData.requirements.map((req, index) => (
								<div key={index} className='flex space-x-2 mb-2'>
									<input
										type='text'
										value={req}
										onChange={(e) => updateArrayItem('requirements', index, e.target.value)}
										className='flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
										placeholder='Requirement...'
									/>
									<button
										type='button'
										onClick={() => removeArrayItem('requirements', index)}
										className='text-red-600 hover:text-red-800'
									>
										<Icon name='Trash2' size={16} />
									</button>
								</div>
							))}
							<button
								type='button'
								onClick={() => addArrayItem('requirements')}
								className='text-[#800020] hover:text-[#6b001a] text-sm'
							>
								+ Add Requirement
							</button>
						</div>

						{/* Responsibilities */}
						<div>
							<label className='block text-sm font-medium text-gray-700 mb-1'>
								Responsibilities
							</label>
							{formData.responsibilities.map((resp, index) => (
								<div key={index} className='flex space-x-2 mb-2'>
									<input
										type='text'
										value={resp}
										onChange={(e) => updateArrayItem('responsibilities', index, e.target.value)}
										className='flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
										placeholder='Responsibility...'
									/>
									<button
										type='button'
										onClick={() => removeArrayItem('responsibilities', index)}
										className='text-red-600 hover:text-red-800'
									>
										<Icon name='Trash2' size={16} />
									</button>
								</div>
							))}
							<button
								type='button'
								onClick={() => addArrayItem('responsibilities')}
								className='text-[#800020] hover:text-[#6b001a] text-sm'
							>
								+ Add Responsibility
							</button>
						</div>

						{/* Benefits */}
						<div>
							<label className='block text-sm font-medium text-gray-700 mb-1'>Benefits</label>
							{formData.benefits.map((benefit, index) => (
								<div key={index} className='flex space-x-2 mb-2'>
									<input
										type='text'
										value={benefit}
										onChange={(e) => updateArrayItem('benefits', index, e.target.value)}
										className='flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
										placeholder='Benefit...'
									/>
									<button
										type='button'
										onClick={() => removeArrayItem('benefits', index)}
										className='text-red-600 hover:text-red-800'
									>
										<Icon name='Trash2' size={16} />
									</button>
								</div>
							))}
							<button
								type='button'
								onClick={() => addArrayItem('benefits')}
								className='text-[#800020] hover:text-[#6b001a] text-sm'
							>
								+ Add Benefit
							</button>
						</div>

						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Salary Range</label>
								<input
									type='text'
									value={formData.salary_range}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, salary_range: e.target.value }))
									}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									placeholder='₦500,000 - ₦800,000'
								/>
							</div>

							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>
									Experience Level
								</label>
								<input
									type='text'
									value={formData.experience_level}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, experience_level: e.target.value }))
									}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									placeholder='3-5 years'
								/>
							</div>
						</div>

						<div>
							<label className='block text-sm font-medium text-gray-700 mb-1'>
								Education Requirements
							</label>
							<input
								type='text'
								value={formData.education_requirements}
								onChange={(e) =>
									setFormData((prev) => ({ ...prev, education_requirements: e.target.value }))
								}
								className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								placeholder='LL.B degree from accredited law school'
							/>
						</div>

						<div>
							<label className='block text-sm font-medium text-gray-700 mb-1'>
								Application Deadline
							</label>
							<input
								type='date'
								value={formData.application_deadline}
								onChange={(e) =>
									setFormData((prev) => ({ ...prev, application_deadline: e.target.value }))
								}
								className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
							/>
						</div>

						<div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
							<div className='flex items-center'>
								<input
									type='checkbox'
									id='is_active'
									checked={formData.is_active}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, is_active: e.target.checked }))
									}
									className='h-4 w-4 text-[#800020] focus:ring-[#800020] border-gray-300 rounded'
								/>
								<label htmlFor='is_active' className='ml-2 block text-sm text-gray-900'>
									Active
								</label>
							</div>

							<div className='flex items-center'>
								<input
									type='checkbox'
									id='is_featured'
									checked={formData.is_featured}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, is_featured: e.target.checked }))
									}
									className='h-4 w-4 text-[#800020] focus:ring-[#800020] border-gray-300 rounded'
								/>
								<label htmlFor='is_featured' className='ml-2 block text-sm text-gray-900'>
									Featured
								</label>
							</div>

							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>
									Display Order
								</label>
								<input
									type='number'
									value={formData.display_order}
									onChange={(e) =>
										setFormData((prev) => ({
											...prev,
											display_order: parseInt(e.target.value) || 0,
										}))
									}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								/>
							</div>
						</div>

						<div className='flex justify-end space-x-3'>
							<button
								type='button'
								onClick={resetForm}
								className='px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors'
							>
								Cancel
							</button>
							<button
								type='submit'
								className='px-4 py-2 bg-[#800020] text-white rounded-md hover:bg-[#6b001a] transition-colors'
							>
								{editingCareer ? 'Update Career' : 'Create Career'}
							</button>
						</div>
					</form>
				</div>
			)}

			{/* Careers List */}
			<div className='bg-white shadow rounded-lg p-6'>
				<h2 className='text-lg font-semibold text-gray-900 mb-4'>Career Opportunities</h2>

				{loading ? (
					<div className='flex items-center justify-center py-8'>
						<Icon name='Loader' className='h-8 w-8 animate-spin text-[#800020]' />
						<span className='ml-2 text-gray-600'>Loading careers...</span>
					</div>
				) : careers.length === 0 ? (
					<div className='text-center py-8 text-gray-500'>
						<Icon name='Briefcase' className='h-12 w-12 mx-auto mb-4 text-gray-300' />
						<p>No career opportunities found</p>
					</div>
				) : (
					<div className='space-y-4'>
						{careers.map((career) => (
							<div key={career.id} className='border border-gray-200 rounded-lg p-4'>
								<div className='flex items-start justify-between'>
									<div className='flex-1'>
										<div className='flex items-center space-x-4'>
											<div className='flex-1'>
												<h3 className='font-medium text-gray-900'>{career.title}</h3>
												<p className='text-sm text-gray-600'>
													{career.department} • {career.location}
												</p>
												<div className='flex items-center mt-2 space-x-4'>
													<span className='text-xs text-gray-500'>
														{career.employment_type.replace('_', ' ')}
													</span>
													<span className='text-xs text-gray-500'>
														{career.salary_range || 'Salary not specified'}
													</span>
													<span className='text-xs text-gray-500'>
														{career.application_deadline || 'No deadline'}
													</span>
													<span
														className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
															career.is_active
																? 'bg-green-100 text-green-800'
																: 'bg-red-100 text-red-800'
														}`}
													>
														{career.is_active ? 'Active' : 'Inactive'}
													</span>
													{career.is_featured && (
														<span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#800020]/10 text-[#800020]'>
															Featured
														</span>
													)}
												</div>
											</div>
										</div>
									</div>
									<div className='flex space-x-2'>
										<button
											onClick={() => handleEdit(career)}
											className='text-[#800020] hover:text-[#6b001a] transition-colors'
										>
											<Icon name='Edit' className='h-4 w-4' />
										</button>
										<button
											onClick={() => handleDelete(career.id)}
											className='text-red-600 hover:text-red-700 transition-colors'
										>
											<Icon name='Trash2' className='h-4 w-4' />
										</button>
									</div>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default CareersManager;
